// Missing Modal Components
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { expensesService } from '../../../services/accounting/expenses';
import LoadingSpinner from '../../ui/LoadingSpinner';
import toast from 'react-hot-toast';

/**
 * CreateExpenseModal Component
 * 
 * Modal for creating new expense entries with category selection,
 * VAT calculations, and form validation.
 * 
 * Features:
 * - Expense category selection with icons
 * - Payment method selection
 * - Automatic VAT calculation
 * - Form validation
 * - Receipt file attachment capability
 * - Arabic/English translation support
 * 
 * @param {Function} onClose - Function to close the modal
 * @param {Function} onSuccess - Function called when expense is created successfully
 * @returns {JSX.Element} The create expense modal interface
 */
const CreateExpenseModal = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { currentCompany, currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    category: '',
    vendor: '',
    description: '',
    amount: '',
    paymentMethod: 'cash',
    reference: '',
    vatRate: 15,
    vatAmount: 0,
    totalAmount: 0,
    notes: '',
    receiptFile: null
  });

  // Load expense categories (default + custom)
  useEffect(() => {
    const loadCategories = () => {
      const defaultCategories = [
        { id: 'office', name: t('expenses.office_expenses', 'Office Expenses'), icon: '🏢' },
        { id: 'marketing', name: t('expenses.marketing_expenses', 'Marketing'), icon: '📢' },
        { id: 'salary', name: t('expenses.salary_expenses', 'Salaries'), icon: '💰' },
        { id: 'utility', name: t('expenses.utility_expenses', 'Utilities'), icon: '⚡' },
        { id: 'equipment', name: t('expenses.equipment_expenses', 'Equipment'), icon: '🔧' },
        { id: 'legal', name: t('expenses.legal_expenses', 'Legal'), icon: '⚖️' },
        { id: 'travel', name: t('expenses.travel_expenses', 'Travel'), icon: '✈️' },
        { id: 'insurance', name: t('expenses.insurance_expenses', 'Insurance'), icon: '🛡️' },
        { id: 'maintenance', name: t('expenses.maintenance_expenses', 'Maintenance'), icon: '🔨' },
        { id: 'fuel', name: t('expenses.fuel_expenses', 'Fuel Expenses'), icon: '⛽' },
        { id: 'truck_washing', name: t('expenses.truck_washing_expenses', 'Truck Washing'), icon: '🚿' },
        { id: 'oil_change', name: t('expenses.oil_change_expenses', 'Oil Changes'), icon: '🛢️' },
        { id: 'other', name: t('expenses.other_expenses', 'Other'), icon: '📋' }
      ];

      try {
        // Load custom categories from localStorage
        const customCategories = JSON.parse(localStorage.getItem(`expense_categories_${currentCompany?.id}`) || '[]');
        
        // Combine default and custom categories
        const allCategories = [...defaultCategories, ...customCategories];
        setExpenseCategories(allCategories);
      } catch (error) {
        console.error('Error loading categories:', error);
        setExpenseCategories(defaultCategories);
      }
    };

    if (currentCompany?.id) {
      loadCategories();
    }
  }, [t, currentCompany?.id]);

  const paymentMethods = [
    { id: 'cash', name: t('expenses.payment.cash', 'Cash'), icon: '💵' },
    { id: 'card', name: t('expenses.payment.card', 'Credit Card'), icon: '💳' },
    { id: 'bank_transfer', name: t('expenses.payment.bank_transfer', 'Bank Transfer'), icon: '🏦' },
    { id: 'check', name: t('expenses.payment.check', 'Check'), icon: '📝' },
    { id: 'online', name: t('expenses.payment.online', 'Online Payment'), icon: '🌐' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      if (field === 'amount' || field === 'vatRate') {
        const amount = parseFloat(newData.amount) || 0;
        const vatRate = parseFloat(newData.vatRate) || 0;
        const vatAmount = amount * (vatRate / 100);
        const totalAmount = amount + vatAmount;
        
        newData.vatAmount = vatAmount;
        newData.totalAmount = totalAmount;
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentCompany?.id || !currentUser?.uid) {
      toast.error(t('common.authentication_required', 'Please login to continue'));
      return;
    }

    setLoading(true);
    try {
      const expenseData = {
        companyId: currentCompany.id,
        date: formData.date,
        category: formData.category,
        vendor: formData.vendor || null,
        description: formData.description,
        amount: parseFloat(formData.amount) || 0,
        paymentMethod: formData.paymentMethod || null,
        reference: formData.reference || null,
        vatRate: parseFloat(formData.vatRate) || 0,
        vatAmount: parseFloat(formData.vatAmount) || 0,
        totalAmount: parseFloat(formData.totalAmount) || 0,
        notes: formData.notes || null,
        status: 'pending',
        createdBy: currentUser.uid,
        createdAt: new Date().toISOString(),
        receiptAttached: false
      };

      await expensesService.addExpense(expenseData);
      toast.success(t('expenses.success.expense_created'));
      onSuccess();
    } catch (error) {
      console.error('Error creating expense:', error);
      toast.error(t('expenses.create_failed', 'Failed to create expense'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold">{t('expenses.create_expense', 'Create Expense')}</h3>
            <button onClick={onClose} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('expenses.date', t('common.date'))} *</label>
              <input
                type="date"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('expenses.category', 'Category')} *</label>
              <select
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
              >
                <option value="">{t('expenses.select_category', 'Select Category')}</option>
                {expenseCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('expenses.vendor', 'Vendor')}</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              value={formData.vendor}
              onChange={(e) => handleInputChange('vendor', e.target.value)}
              placeholder={t('expenses.vendor_placeholder', 'Vendor name (optional)')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('expenses.description', t('common.description'))} *</label>
            <textarea
              required
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder={t('expenses.description_placeholder', 'Describe the expense')}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('expenses.amount', t('common.amount'))} (SAR) *</label>
              <input
                type="number"
                step="0.01"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                value={formData.amount}
                onChange={(e) => handleInputChange('amount', e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('expenses.payment_method', 'Payment Method')}</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                value={formData.paymentMethod}
                onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
              >
                {paymentMethods.map(method => (
                  <option key={method.id} value={method.id}>{method.icon} {method.name}</option>
                ))}
              </select>
            </div>
          </div>
        </form>

        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-2">
          <button type="button" onClick={onClose} className="btn btn-outline">
            {t('common.cancel', t('common.cancel'))}
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading}
            className="btn btn-primary"
          >
            {loading ? (
              <>
                <LoadingSpinner size="sm" />
                {t('expenses.creating', 'Creating...')}
              </>
            ) : (
              t('expenses.create_expense', 'Create Expense')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateExpenseModal;
